import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import StatCard from '../../components/ui/StatCard';
import ActionCard from '../../components/ui/ActionCard';
import Logo from '../../components/Logo';
import {
  Activity,
  Users,
  DollarSign,
  Package,
  Clock,
  TrendingUp,
  AlertCircle,
  BarChart3,
  FileText,
  Settings
} from 'lucide-react';

interface PlatformStats {
  total_users: number;
  total_groups: number;
  active_groups: number;
  total_revenue: number;
  pending_payments: number;
}

interface PlanStat {
  plan_id: string;
  plan_name: string;
  plan_type: string;
  price_vatu: number;
  active_subscriptions: number;
  total_revenue: number;
}

interface RecentActivity {
  id: string;
  action_type: string;
  entity_type: string;
  details: any;
  created_at: string;
  admin: {
    full_name: string;
  };
}

const SuperAdminDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [planStats, setPlanStats] = useState<PlanStat[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data
  useEffect(() => {
    if (!user || !userProfile || userProfile.role !== 'super_admin') return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch platform statistics from Supabase
        let stats = {
          total_users: 0,
          total_groups: 0,
          active_groups: 0,
          total_revenue: 0,
          pending_payments: 0
        };

        try {
          const { data: statsData, error: statsError } = await supabase
            .rpc('get_platform_statistics');

          if (!statsError && statsData) {
            // Handle RPC response - it returns a single JSONB object
            const rpcStats = Array.isArray(statsData) ? statsData[0] : statsData;
            if (rpcStats) {
              stats = rpcStats;
            }
          } else {
            // Fallback: Query data directly if RPC function doesn't exist
            console.warn('RPC function not available, using direct queries:', statsError);
            
            // Count total users
            const { count: totalUsers } = await supabase
              .from('user_profiles')
              .select('*', { count: 'exact', head: true });
            
            // Count groups
            const { count: totalGroups } = await supabase
              .from('groups')
              .select('*', { count: 'exact', head: true });
            
            const { count: activeGroups } = await supabase
              .from('groups')
              .select('*', { count: 'exact', head: true })
              .eq('is_active', true);
            
            // Get revenue from payment transactions
            const { data: revenueData } = await supabase
              .from('payment_transactions')
              .select('amount_vatu')
              .eq('payment_status', 'approved');
            
            const totalRevenue = revenueData?.reduce((sum, p) => sum + Number(p.amount_vatu || 0), 0) || 0;
            
            // Count pending payments
            const { count: pendingPayments } = await supabase
              .from('payment_transactions')
              .select('*', { count: 'exact', head: true })
              .eq('payment_status', 'pending');
            
            stats = {
              total_users: totalUsers || 0,
              total_groups: totalGroups || 0,
              active_groups: activeGroups || 0,
              total_revenue: totalRevenue,
              pending_payments: pendingPayments || 0
            };
          }
        } catch (rpcError: any) {
          console.warn('RPC call failed, using direct queries:', rpcError);
          
          // Fallback: Query data directly
          const { count: totalUsers } = await supabase
            .from('user_profiles')
            .select('*', { count: 'exact', head: true });
          
          const { count: totalGroups } = await supabase
            .from('groups')
            .select('*', { count: 'exact', head: true });
          
          const { count: activeGroups } = await supabase
            .from('groups')
            .select('*', { count: 'exact', head: true })
            .eq('is_active', true);
          
          const { data: revenueData } = await supabase
            .from('payment_transactions')
            .select('amount_vatu')
            .eq('payment_status', 'approved');
          
          const totalRevenue = revenueData?.reduce((sum, p) => sum + Number(p.amount_vatu || 0), 0) || 0;
          
          const { count: pendingPayments } = await supabase
            .from('payment_transactions')
            .select('*', { count: 'exact', head: true })
            .eq('payment_status', 'pending');
          
          stats = {
            total_users: totalUsers || 0,
            total_groups: totalGroups || 0,
            active_groups: activeGroups || 0,
            total_revenue: totalRevenue,
            pending_payments: pendingPayments || 0
          };
        }
        
        setStats(stats);

        // Fetch plan statistics from Supabase
        let planStatsData: PlanStat[] = [];
        
        try {
          const { data: planData, error: planError } = await supabase
            .rpc('get_plan_statistics');

          if (!planError && planData) {
            planStatsData = planData;
          } else {
            // Fallback: Query plan statistics directly
            console.warn('RPC function not available, using direct queries:', planError);
            
            const { data: plans } = await supabase
              .from('subscription_plans')
              .select('id, plan_name, plan_type, price_vatu')
              .order('created_at', { ascending: false });
            
            if (plans) {
              for (const plan of plans) {
                // Get active subscriptions count
                const { count: activeSubs } = await supabase
                  .from('user_subscriptions')
                  .select('*', { count: 'exact', head: true })
                  .eq('plan_id', plan.id)
                  .eq('subscription_status', 'active');
                
                // Get total revenue for this plan
              // Get payments for subscriptions with this plan
              const { data: subscriptions } = await supabase
                .from('user_subscriptions')
                .select('id')
                .eq('plan_id', plan.id);
              
              const subscriptionIds = subscriptions?.map(s => s.id) || [];
              
              let totalRevenue = 0;
              if (subscriptionIds.length > 0) {
                const { data: payments } = await supabase
                  .from('payment_transactions')
                  .select('amount_vatu')
                  .in('subscription_id', subscriptionIds)
                  .eq('payment_status', 'approved');
                
                totalRevenue = payments?.reduce((sum, p) => sum + Number(p.amount_vatu || 0), 0) || 0;
              }
                
                planStatsData.push({
                  plan_id: plan.id,
                  plan_name: plan.plan_name,
                  plan_type: plan.plan_type,
                  price_vatu: plan.price_vatu,
                  active_subscriptions: activeSubs || 0,
                  total_revenue: totalRevenue
                });
              }
            }
          }
        } catch (planRpcError: any) {
          console.warn('Plan RPC call failed, using direct queries:', planRpcError);
          
          // Fallback: Query plan statistics directly
          const { data: plans } = await supabase
            .from('subscription_plans')
            .select('id, plan_name, plan_type, price_vatu')
            .order('created_at', { ascending: false });
          
          if (plans) {
            for (const plan of plans) {
              const { count: activeSubs } = await supabase
                .from('user_subscriptions')
                .select('*', { count: 'exact', head: true })
                .eq('plan_id', plan.id)
                .eq('subscription_status', 'active');
              
              // Get payments for subscriptions with this plan
              const { data: subscriptions } = await supabase
                .from('user_subscriptions')
                .select('id')
                .eq('plan_id', plan.id);
              
              const subscriptionIds = subscriptions?.map(s => s.id) || [];
              
              let totalRevenue = 0;
              if (subscriptionIds.length > 0) {
                const { data: payments } = await supabase
                  .from('payment_transactions')
                  .select('amount_vatu')
                  .in('subscription_id', subscriptionIds)
                  .eq('payment_status', 'approved');
                
                totalRevenue = payments?.reduce((sum, p) => sum + Number(p.amount_vatu || 0), 0) || 0;
              }
              
              planStatsData.push({
                plan_id: plan.id,
                plan_name: plan.plan_name,
                plan_type: plan.plan_type,
                price_vatu: plan.price_vatu,
                active_subscriptions: activeSubs || 0,
                total_revenue: totalRevenue
              });
            }
          }
        }
        
        setPlanStats(planStatsData);

        // Fetch recent audit logs with error handling
        // Try with admin_id first (super admin audit system schema)
        let activityData: any[] | null = null;
        let activityError: any = null;
        
        try {
          const result = await supabase
            .from('audit_logs')
            .select('id, admin_id, action_type, entity_type, details, created_at')
            .order('created_at', { ascending: false })
            .limit(10);
          
          activityData = result.data;
          activityError = result.error;
          
          // If admin_id doesn't work, try user_id (alternative schema)
          if (activityError && activityError.code === 'PGRST116' || activityError?.message?.includes('column')) {
            const altResult = await supabase
              .from('audit_logs')
              .select('id, user_id, action_type, action_description, created_at')
              .order('created_at', { ascending: false })
              .limit(10);
            
            activityData = altResult.data;
            activityError = altResult.error;
            
            // Transform user_id schema to match expected format
            if (activityData && activityData.length > 0) {
              activityData = activityData.map((a: any) => ({
                id: a.id,
                admin_id: a.user_id, // Map user_id to admin_id for consistency
                action_type: a.action_type,
                entity_type: 'general', // Default since this schema doesn't have entity_type
                details: { description: a.action_description || '' },
                created_at: a.created_at
              }));
            }
          }
        } catch (err: any) {
          console.warn('Failed to fetch audit logs:', err);
          activityError = err;
        }

        if (activityError) {
          console.warn('Error fetching audit logs, using empty array:', activityError);
          setRecentActivity([]);
        } else if (activityData && activityData.length > 0) {
          // Fetch admin names separately and join them
          const adminIds = [...new Set(activityData.map((a: any) => a.admin_id).filter(Boolean))];
          
          let adminMap: Record<string, { full_name: string }> = {};
          if (adminIds.length > 0) {
            const { data: admins } = await supabase
              .from('user_profiles')
              .select('id, full_name')
              .in('id', adminIds);
            
            if (admins) {
              adminMap = admins.reduce((acc, admin) => {
                acc[admin.id] = { full_name: admin.full_name || 'Unknown Admin' };
                return acc;
              }, {} as Record<string, { full_name: string }>);
            }
          }
          
          // Map activity data with admin information
          const activityWithAdmins = activityData.map((activity: any) => ({
            id: activity.id,
            action_type: activity.action_type,
            entity_type: activity.entity_type || 'general',
            details: activity.details || {},
            created_at: activity.created_at,
            admin: adminMap[activity.admin_id] || { full_name: 'Unknown Admin' }
          }));
          
          setRecentActivity(activityWithAdmins);
        } else {
          setRecentActivity([]);
        }

      } catch (err: any) {
        console.error('Error fetching super admin dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, userProfile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading super admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive mb-4">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Super Admin Dashboard - communitySaving</title>
        <meta
          name="description"
          content="Super admin dashboard for platform-wide management, subscription oversight, and system analytics"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
        />

        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar isCollapsed={isSidebarCollapsed} userRole="admin" isSuperAdmin={true} />
          </div>

          <main
            className={`flex-1 transition-all duration-300 ${
              isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
            }`}
          >
            <div className="p-4 lg:p-6 max-w-7xl mx-auto">
              <div className="mb-6">
                <BreadcrumbTrail />
              </div>

              {/* Welcome Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Logo size={40} color="gradient" variant="icon" />
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                      Super Admin Dashboard
                    </h1>
                    <p className="text-slate-600 mt-1">
                      Welcome back! Here's your platform overview
                    </p>
                  </div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                  title="Total Users"
                  value={stats?.total_users || 0}
                  icon={Users}
                  iconColor="text-blue-600"
                  iconBgColor="bg-blue-100"
                  subtitle="Registered members"
                />
                <StatCard
                  title="Active Groups"
                  value={stats?.active_groups || 0}
                  icon={Package}
                  iconColor="text-emerald-600"
                  iconBgColor="bg-emerald-100"
                  subtitle={`of ${stats?.total_groups || 0} total groups`}
                />
                <StatCard
                  title="Total Revenue"
                  value={`${stats?.total_revenue ? stats.total_revenue.toLocaleString() : '0'} VT`}
                  icon={DollarSign}
                  iconColor="text-purple-600"
                  iconBgColor="bg-purple-100"
                  subtitle="All-time earnings"
                />
                <StatCard
                  title="Pending Payments"
                  value={stats?.pending_payments || 0}
                  icon={Clock}
                  iconColor="text-amber-600"
                  iconBgColor="bg-amber-100"
                  subtitle="Awaiting approval"
                />
              </div>

              {/* Active Subscriptions Card */}
              <div className="mb-8">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-strong p-8 text-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                          <TrendingUp className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-semibold">Active Subscriptions</h3>
                      </div>
                      <p className="text-5xl font-bold mb-2">
                        {planStats.reduce((sum, p) => sum + p.active_subscriptions, 0)}
                      </p>
                      <p className="text-white/80">Across all subscription plans</p>
                    </div>
                    <div className="hidden md:block">
                      <svg className="w-32 h-32 opacity-20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M13 10h-2V8h2v2zm0-4h-2V1h2v5zM9 10H7V8h2v2zm0-4H7V1h2v5zm10 9h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V5h2v2zm-4 12h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <ActionCard
                    title="Manage Plans"
                    description="Create and edit subscription plans"
                    icon={Package}
                    iconColor="text-blue-600"
                    onClick={() => navigate('/subscription-plan-management')}
                  />
                  <ActionCard
                    title="Review Payments"
                    description="Approve or reject payment submissions"
                    icon={DollarSign}
                    iconColor="text-emerald-600"
                    onClick={() => navigate('/payment-processing')}
                  />
                  <ActionCard
                    title="Manage Groups"
                    description="Activate, deactivate, or modify groups"
                    icon={Users}
                    iconColor="text-purple-600"
                    onClick={() => navigate('/group-management')}
                  />
                  <ActionCard
                    title="View Reports"
                    description="Access system-wide analytics"
                    icon={BarChart3}
                    iconColor="text-amber-600"
                    onClick={() => navigate('/reports')}
                  />
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Plan Statistics */}
                <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Subscription Plans</h2>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
                    {planStats.map((plan) => (
                      <div key={plan.plan_id} className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-100">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">{plan.plan_name}</h3>
                            <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mt-1">
                              {plan.plan_type}
                            </span>
                          </div>
                          <span className="text-lg font-bold text-blue-600">
                            {plan.price_vatu.toLocaleString()} VT
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-3 pt-3 border-t border-gray-200">
                          <span className="text-gray-600">
                            <span className="font-semibold text-gray-900">{plan.active_subscriptions}</span> subscriptions
                          </span>
                          <span className="text-gray-600">
                            Revenue: <span className="font-semibold text-gray-900">{plan.total_revenue.toLocaleString()} VT</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Activity className="w-5 h-5 text-purple-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
                    {recentActivity.length > 0 ? (
                      recentActivity.map((activity) => {
                        const admin = activity.admin as any;
                        return (
                          <div key={activity.id} className="flex items-start gap-3 p-4 bg-gradient-to-r from-gray-50 to-purple-50 rounded-xl border border-gray-100">
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                              <Activity className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900">
                                {activity.action_type.replace(/_/g, ' ')}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                by {admin?.full_name || 'System'} • {activity.entity_type}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(activity.created_at).toLocaleDateString()} at {new Date(activity.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12">
                        <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No recent activity</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Toggle Sidebar Button */}
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex fixed bottom-6 left-6 w-12 h-12 items-center justify-center bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-full shadow-strong hover:shadow-xl transition-all duration-300 hover:scale-110 z-50"
                aria-label="Toggle sidebar"
              >
                <span className="text-lg font-bold">
                  {isSidebarCollapsed ? '→' : '←'}
                </span>
              </button>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default SuperAdminDashboard;